import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  ClipboardList,
  Trash2,
  ShoppingCart,
  Send,
  Loader2,
  Package,
  CalendarDays,
  MapPin,
  Users,
  Phone,
  Mail,
  MessageSquare,
  Plus,
  Minus,
  RotateCcw,
} from "lucide-react";
import { QuoteEstimate } from "@/components/site/QuoteEstimate";
import {
  DEFAULT_QUOTE,
  composeInquiryMessage,
  INQUIRY_MESSAGE_LIMIT,
} from "@/lib/quote-context";
import { useInquiry } from "@/lib/inquiry-store";
import { submitInquiry } from "@/lib/inquiry.functions";
import { upsertInquiryDraft, deleteInquiryDraft } from "@/lib/inquiry-draft.functions";
import { getDraftSessionId } from "@/lib/draft-session";

export const Route = createFileRoute("/forfragan")({
  head: () => ({
    meta: [
      { title: "Min förfrågan — Event Sverige" },
      {
        name: "description",
        content:
          "Skicka din förfrågan till Event Sverige. Förfrågan sparas, granskas av vårt team och besvaras med pris och tillgänglighet.",
      },
      { property: "og:title", content: "Min förfrågan — Event Sverige" },
      {
        property: "og:description",
        content: "Skicka din förfrågan. Vårt team granskar upplägget och återkommer med pris och tillgänglighet.",
      },
      { property: "og:url", content: "https://eventsverige.se/forfragan" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      {
        name: "twitter:description",
        content: "Skicka din förfrågan. Vårt team granskar upplägget och återkommer med pris och tillgänglighet.",
      },
    ],
    links: [{ rel: "canonical", href: "https://eventsverige.se/forfragan" }],
  }),
  component: ForfraganPage,
});

const DRAFT_LS_KEY = "inquiry_draft_form_v1";

type FormState = {
  name: string;
  email: string;
  phone: string;
  eventDate: string;
  eventDateEnd: string;
  location: string;
  guests: string;
  message: string;
};

const EMPTY: FormState = {
  name: "",
  email: "",
  phone: "",
  eventDate: "",
  eventDateEnd: "",
  location: "",
  guests: "",
  message: "",
};

function formatDateRange(start: string, end: string): string {
  if (start && end && start !== end) return `${start} → ${end}`;
  return start;
}

function loadLocalDraft(): FormState {
  if (typeof window === "undefined") return EMPTY;
  try {
    const raw = localStorage.getItem(DRAFT_LS_KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw);
    return { ...EMPTY, ...parsed };
  } catch {
    return EMPTY;
  }
}

function ForfraganPage() {
  const navigate = useNavigate();
  const { items, remove, setQty, clear } = useInquiry();
  const [quoteOptions, setQuoteOptions] = useState(DEFAULT_QUOTE);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [hydrated, setHydrated] = useState(false);
  const upsertDraft = useServerFn(upsertInquiryDraft);
  const removeDraft = useServerFn(deleteInquiryDraft);
  const sessionIdRef = useRef<string>("");
  const debounceRef = useRef<number | null>(null);
  const messageComposition = composeInquiryMessage(form.message, items, quoteOptions);

  // Hydrate from localStorage on mount (client only)
  useEffect(() => {
    setForm(loadLocalDraft());
    sessionIdRef.current = getDraftSessionId();
    setHydrated(true);
  }, []);

  // Auto-save (debounced) to localStorage + DB
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(DRAFT_LS_KEY, JSON.stringify(form));
    } catch {
      // ignore
    }

    const hasContent =
      form.name.trim() ||
      form.email.trim() ||
      form.phone.trim() ||
      form.message.trim() ||
      form.location.trim() ||
      form.eventDate.trim() ||
      form.eventDateEnd.trim() ||
      form.guests.trim() ||
      items.length > 0;
    if (!hasContent || !sessionIdRef.current) return;

    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      upsertDraft({
        data: {
          session_id: sessionIdRef.current,
          name: form.name,
          email: form.email,
          phone: form.phone,
          event_date: formatDateRange(form.eventDate, form.eventDateEnd),
          location: form.location,
          guests: Number(form.guests) || 0,
          message: messageComposition.valid ? messageComposition.composed : form.message,
          products: items.map((it) => ({
            slug: it.slug,
            name: it.name,
            image: it.image,
            quantity: it.quantity,
          })),
        },
      }).catch(() => {
        // silent — drafts are best-effort
      });
    }, 700);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [form, items, quoteOptions, hydrated, upsertDraft, messageComposition.composed, messageComposition.valid]);

  const update = (field: keyof FormState, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const resetMessage = () => {
    setForm((f) => ({ ...f, message: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Fyll i namn och e-post");
      return;
    }
    if (!form.phone.trim()) {
      toast.error("Telefonnummer är obligatoriskt");
      return;
    }
    if (!form.message.trim()) {
      toast.error("Beskriv ditt event så vi kan ge dig bästa offert");
      return;
    }
    if (!messageComposition.valid) {
      toast.error(`Korta beskrivningen med minst ${Math.abs(messageComposition.remaining)} tecken. Koncept- och offertunderlaget måste få plats.`);
      return;
    }
    setLoading(true);
    try {
      await submitInquiry({
        data: {
          name: form.name,
          email: form.email,
          phone: form.phone,
          company: "",
          eventType: "",
          eventDate: formatDateRange(form.eventDate, form.eventDateEnd),
          location: form.location,
          guests: Number(form.guests) || 0,
          message: messageComposition.composed,
          products: items.map((it) => ({
            slug: it.slug,
            name: it.name,
            image: it.image,
            quantity: it.quantity,
          })),
          session_id: sessionIdRef.current || undefined,
        },
      });
      // Clear local draft + remote draft
      try {
        localStorage.removeItem(DRAFT_LS_KEY);
      } catch {
        /* ignore */
      }
      if (sessionIdRef.current) {
        removeDraft({ data: { session_id: sessionIdRef.current } }).catch(() => {});
      }
      clear();
      setForm(EMPTY);
      toast.success("Förfrågan mottagen! Vi återkommer med pris och tillgänglighet.");
      navigate({ to: "/tack" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Något gick fel");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 bg-grain opacity-20" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 text-accent px-4 py-1.5 text-xs font-bold uppercase tracking-widest mb-6">
              <ClipboardList className="h-3.5 w-3.5" />
              Min förfrågan
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold leading-[0.95]">
              Din <span className="text-accent">önskelista.</span>
            </h1>
            <p className="mt-4 text-lg text-primary-foreground/80 max-w-xl">
              {items.length > 0
                ? `Du har valt ${items.length} koncept eller aktivitet. Fyll i uppgifterna nedan så återkommer vi med offert.`
                : "Berätta vad ni vill göra. Ni kan välja ett koncept eller be oss ta fram ett upplägg."}
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid gap-10 lg:grid-cols-2">
          {/* Selected products */}
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-bold flex items-center gap-2">
                <ShoppingCart className="h-6 w-6 text-accent" />
                Valda koncept & aktiviteter
              </h2>
              {items.length > 0 && (
                <button
                  onClick={clear}
                  className="text-sm text-destructive font-medium hover:underline"
                >
                  Töm lista
                </button>
              )}
            </div>

            {items.length === 0 ? (
              <div className="rounded-2xl bg-card border border-border p-10 text-center">
                <div className="mx-auto h-14 w-14 rounded-full bg-secondary grid place-items-center mb-4">
                  <Package className="h-7 w-7 text-muted-foreground" />
                </div>
                <h3 className="font-display text-lg font-bold">Hitta ert upplägg</h3>
                <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto">
                  Välj ett koncept, eller beskriv era önskemål direkt i formuläret.
                </p>
                <Link
                  to="/koncept"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-5 py-2.5 text-sm font-bold hover:bg-accent/90 transition"
                >
                  <Plus className="h-4 w-4" />
                  Utforska koncept
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {items.map((item) => (
                  <div
                    key={item.slug}
                    className="flex items-center gap-4 rounded-2xl bg-card border border-border p-4"
                  >
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="h-16 w-16 rounded-xl object-cover bg-secondary shrink-0"
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-xl bg-primary text-primary-foreground grid place-items-center shrink-0" aria-hidden>
                        <Package className="h-6 w-6" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm truncate">{item.name}</h4>
                      <div className="mt-2 inline-flex items-center gap-1 rounded-full border border-border bg-background p-1">
                        <button
                          type="button"
                          onClick={() => setQty(item.slug, item.quantity - 1)}
                          className="h-7 w-7 grid place-items-center rounded-full hover:bg-secondary transition"
                          aria-label="Minska antal"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <input
                          type="number"
                          min={1}
                          value={item.quantity}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (!Number.isNaN(v)) setQty(item.slug, v);
                          }}
                          className="w-10 bg-transparent text-center text-sm font-semibold focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          aria-label={`Antal ${item.name}`}
                        />
                        <button
                          type="button"
                          onClick={() => setQty(item.slug, item.quantity + 1)}
                          className="h-7 w-7 grid place-items-center rounded-full hover:bg-secondary transition"
                          aria-label="Öka antal"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => remove(item.slug)}
                      className="p-2 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition shrink-0"
                      aria-label={`Ta bort ${item.name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <Link
                  to="/koncept"
                  className="inline-flex items-center gap-2 rounded-full bg-secondary px-5 py-2.5 text-sm font-semibold hover:bg-secondary/70 transition"
                >
                  <Plus className="h-4 w-4" />
                  Lägg till fler koncept
                </Link>
              </div>
            )}

            {/* Quick links */}
            <div className="mt-8 rounded-2xl bg-secondary/50 border border-border p-6">
              <h3 className="font-display text-lg font-bold">Snabblänkar</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link
                  to="/koncept"
                  className="rounded-full bg-card border border-border px-3 py-1.5 text-xs font-medium hover:border-primary/30 transition"
                >
                  Eventkoncept
                </Link>
                <Link
                  to="/tivolispel"
                  className="rounded-full bg-card border border-border px-3 py-1.5 text-xs font-medium hover:border-primary/30 transition"
                >
                  Tivolispel
                </Link>
                <Link
                  to="/scen-ljud-ljus"
                  className="rounded-full bg-card border border-border px-3 py-1.5 text-xs font-medium hover:border-primary/30 transition"
                >
                  Scen & ljud
                </Link>
                <Link
                  to="/foretag"
                  className="rounded-full bg-card border border-border px-3 py-1.5 text-xs font-medium hover:border-primary/30 transition"
                >
                  Företag
                </Link>
              </div>
            </div>
          </div>

          {/* Form */}
          <div>
            <QuoteEstimate items={items} options={quoteOptions} onChange={setQuoteOptions} />
            <h2 className="font-display text-2xl font-bold mb-6 flex items-center gap-2">
              <Send className="h-6 w-6 text-accent" />
              Skicka förfrågan
            </h2>
            <form
              onSubmit={handleSubmit}
              className="rounded-2xl bg-card border border-border p-6 sm:p-8 space-y-5"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold mb-1.5">
                    Namn <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="name"
                    type="text"
                    required
                    maxLength={200}
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Anna Svensson"
                    className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold mb-1.5">
                    <Mail className="h-3.5 w-3.5 inline mr-1" />
                    E-post <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    maxLength={320}
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="anna@exempel.se"
                    className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold mb-1.5">
                    <Phone className="h-3.5 w-3.5 inline mr-1" />
                    Telefon <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    required
                    maxLength={50}
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="070-123 45 67"
                    className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label htmlFor="guests" className="block text-sm font-semibold mb-1.5">
                    <Users className="h-3.5 w-3.5 inline mr-1" />
                    Antal gäster
                  </label>
                  <input
                    id="guests"
                    type="number"
                    min={1}
                    value={form.guests}
                    onChange={(e) => update("guests", e.target.value)}
                    placeholder="50"
                    className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>

              {/* När? Datum-block */}
              <div className="rounded-xl border border-border bg-secondary/40 p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-3">
                  <CalendarDays className="h-4 w-4 text-accent" />
                  <h3 className="text-sm font-bold">När är eventet?</h3>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="eventDate"
                      className="block text-xs font-semibold text-muted-foreground mb-1.5"
                    >
                      Från
                    </label>
                    <input
                      id="eventDate"
                      type="date"
                      value={form.eventDate}
                      onChange={(e) => {
                        update("eventDate", e.target.value);
                        if (form.eventDateEnd && e.target.value > form.eventDateEnd) {
                          update("eventDateEnd", "");
                        }
                      }}
                      className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="eventDateEnd"
                      className="block text-xs font-semibold text-muted-foreground mb-1.5"
                    >
                      Till <span className="font-normal">(valfritt)</span>
                    </label>
                    <input
                      id="eventDateEnd"
                      type="date"
                      min={form.eventDate || undefined}
                      value={form.eventDateEnd}
                      onChange={(e) => update("eventDateEnd", e.target.value)}
                      className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Engångsevent? Fyll bara i <em>Från</em>. För flera dagar — ange både <em>Från</em>{" "}
                  och <em>Till</em>.
                </p>
              </div>

              <div>
                <label htmlFor="location" className="block text-sm font-semibold mb-1.5">
                  <MapPin className="h-3.5 w-3.5 inline mr-1" />
                  Plats / Ort
                </label>
                <input
                  id="location"
                  type="text"
                  maxLength={300}
                  value={form.location}
                  onChange={(e) => update("location", e.target.value)}
                  placeholder="Stockholm"
                  className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="message" className="block text-sm font-semibold">
                    <MessageSquare className="h-3.5 w-3.5 inline mr-1" />
                    Beskriv ditt event <span className="text-red-500">*</span>
                  </label>
                  {form.message && (
                    <button
                      type="button"
                      onClick={resetMessage}
                      className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-destructive transition"
                    >
                      <RotateCcw className="h-3 w-3" />
                      Återställ
                    </button>
                  )}
                </div>
                <textarea
                  id="message"
                  rows={5}
                  required
                  maxLength={INQUIRY_MESSAGE_LIMIT}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Berätta lite om eventet — t.ex. typ av tillställning, åldersgrupp, plats/lokal (inne/ute), tider, om ni behöver hjälp med uppställning eller har specifika önskemål."
                  className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                />
                <p className={`mt-1.5 text-xs ${messageComposition.valid ? "text-muted-foreground" : "text-destructive font-semibold"}`}>
                  {messageComposition.valid
                    ? `${messageComposition.remaining.toLocaleString("sv-SE")} tecken kvar inklusive koncept- och offertunderlag.`
                    : `Korta beskrivningen med minst ${Math.abs(messageComposition.remaining).toLocaleString("sv-SE")} tecken. Ändrade val kan påverka utrymmet.`}
                </p>
                {hydrated && (form.name || form.email || form.message) && (
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    ✓ Sparas automatiskt — fyll i nu, skicka senare.
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !messageComposition.valid}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3.5 text-base font-bold hover:bg-accent/90 transition disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Skickar...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    Skicka förfrågan
                  </>
                )}
              </button>

              <p className="text-center text-xs text-muted-foreground">
                Det är kostnadsfritt att skicka en förfrågan. Bokningen gäller först efter bekräftad
                offert.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
